/**
 * Fonte ÚNICA de valor visual (ART-DIRECTION M1).
 * É a mesma decisão de packages/gamedata aplicada à arte: o valor mora em um lugar,
 * é validado, versionado, e mudança dispara diff revisável.
 *
 * NEVER escreva cor, easing ou duração literal em componente ou shader —
 * a regra de lint `design-tokens-only` bloqueia.
 *
 * ⚠️ VALORES PROVISÓRIOS. A direção de arte real é decidida na fase 03, a partir
 * do board de referência (ART-DIRECTION M2). A ESTRUTURA aqui é definitiva;
 * os números serão substituídos. Existe agora para que o HUD da fase 00
 * não nasça com literal e vire precedente (lição do REV-14).
 */

/** Rampa neutra com viés de matiz, NEVER cinza puro (ART-DIRECTION, lista de proibições). */
const neutral = {
  0: '#0B1013',
  1: '#141B20',
  2: '#1D262C',
  3: '#2A353D',
  4: '#3D4A53',
  5: '#5C6B75',
  6: '#8A9AA4',
  7: '#B9C6CE',
  8: '#DDE5EA',
  9: '#F2F6F8',
} as const;

const accent = {
  base: '#1F8F9B',
  bright: '#4FCFDC',
  dim: '#0E3237',
} as const;

const semantic = {
  ok: '#3FA875',
  warn: '#C98A2B',
  danger: '#C6584A',
} as const;

export const tokens = {
  color: {
    neutral,
    accent,
    semantic,
    /** Papéis. Componente ALWAYS usa papel, NEVER a rampa direta. */
    role: {
      canvasBg: neutral[0],
      surface: neutral[1],
      surfaceRaised: neutral[2],
      /** Divisória decorativa. Sutil de propósito; NEVER use para borda de controle. */
      border: neutral[3],
      /**
       * Borda de controle interativo, cuja forma comunica estado.
       * ALWAYS >= 3:1 contra a superfície (WCAG 1.4.11) — verificado em teste.
       */
      controlBorder: neutral[5],
      textPrimary: neutral[9],
      textSecondary: neutral[7],
      textMuted: neutral[6],
      accentText: accent.bright,
      accentSurface: accent.dim,
      focusRing: accent.bright,
    },
  },

  type: {
    /**
     * ⚠️ Placeholder até a fase 03. Fonte default é o tell nº 1 de UI de IA.
     * Duas famílias self-hosted em woff2 subsetado (ART-DIRECTION M3).
     */
    family: {
      display: 'var(--font-display)',
      ui: 'var(--font-ui)',
      /**
       * Numeral tabular obrigatório: sem isso placar e cronômetro tremem.
       * O fallback é monoespaçado de propósito: enquanto a fase 03 não define a
       * fonte, o HUD cai em algo que ALINHA número, NEVER no serif do browser.
       */
      numeric: 'var(--font-numeric, ui-monospace, monospace)',
    },
    size: { xs: 12, sm: 14, base: 16, lg: 20, xl: 26, '2xl': 34, '3xl': 44 },
    weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
    leading: { tight: 1.15, normal: 1.5, relaxed: 1.65 },
    tracking: { tight: '-0.02em', normal: '0', wide: '0.08em' },
  },

  /** Escala de espaçamento em px. NEVER use valor fora da escala. */
  space: { 0: 0, 1: 4, 2: 8, 3: 12, 4: 16, 5: 24, 6: 32, 7: 48, 8: 64 },

  /** Raios diferentes por propósito: raio igual em tudo é ausência de hierarquia. */
  radius: { none: 0, control: 6, panel: 12, pill: 999 },

  motion: {
    duration: { instant: 80, fast: 140, base: 220, slow: 340 },
    easing: {
      /** Entrada de elemento: rápido no começo, assenta no fim. */
      out: 'cubic-bezier(0.16, 1, 0.3, 1)',
      /** Saída: começa devagar, acelera pra fora. */
      in: 'cubic-bezier(0.7, 0, 0.84, 0)',
      /** Movimento contínuo. */
      inOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
    },
    /** O que NEVER anima: mudança de layout durante partida, e nada em reduced-motion. */
  },

  layer: { world: 0, hud: 10, overlay: 20, modal: 30, toast: 40 },

  /** Alvo de toque mínimo em mobile. Verificado em teste (ART-DIRECTION M7). */
  touch: { minTarget: 44 },

  /**
   * O mundo 3D. ART-DIRECTION M5: o look vira PARÂMETRO, e NEVER improviso por
   * cena — é comprometer-se com uma receita que produz identidade.
   *
   * Só o que é VISUAL mora aqui. Nada nesta seção pode afetar o sim: raio de
   * colisão, alcance e velocidade são `packages/gamedata` (invariante 6), e um
   * número daqui vazando para regra de jogo quebraria os dois donos ao mesmo tempo.
   */
  world: {
    /**
     * Altura do avatar em unidades de mundo.
     *
     * Maior que o diâmetro de colisão (`player.radius * 2` = 1) de propósito: a
     * silhueta de personagem tem que ler como personagem numa câmera de cima e
     * distante, e uma malha da altura exata da caixa lê como caixa. O que a
     * silhueta NEVER pode fazer é mentir sobre a LARGURA — por isso a escala é
     * pela altura, e o robô continua mais estreito que o próprio raio.
     */
    avatarHeight: 1.4,
    /**
     * Correção de guinada da malha, em radianos: quanto girar para a FRENTE do
     * modelo coincidir com a mira do jogador.
     *
     * Existe porque "frente" é convenção de quem esculpiu, e NEVER um fato do
     * formato — glTF não tem campo para isso. Deixar a correção aqui é o que
     * evita que a alternativa aconteça: girar a malha no arquivo, que esconde a
     * convenção dentro de um binário onde ninguém acha.
     */
    avatarYaw: 0,
  },
} as const;

export type Tokens = typeof tokens;
export type ColorRole = keyof typeof tokens.color.role;
